/**
 * Quick Navigation Component
 *
 * Sticky navigation menu that appears on the right side (desktop)
 * or as vertical dots on the left (mobile/tablet).
 *
 * Automatically tracks scroll position and highlights current section.
 * Detects page pathname to show appropriate navigation sections.
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle } from 'lucide-react';

interface NavSection {
  id: string;
  title: string;
  element: string; // ID of the section element
}

export function QuickNavigation() {
  const location = useLocation();

  // Define navigation sections based on current page
  const getNavSections = (): NavSection[] => {
    // City Partnership page sections
    if (location.pathname === '/city-partnership') {
      return [
        { id: 'nav-revenue-model', title: 'Revenue Model', element: 'revenue-model' },
        {
          id: 'nav-city-paid-model',
          title: 'City-Paid Universal Access',
          element: 'city-paid-model',
        },
        {
          id: 'nav-legislative-mandate',
          title: 'Legislative Mandate',
          element: 'legislative-mandate',
        },
        { id: 'nav-phase-2-expansion', title: 'Phase 2 Expansion', element: 'phase-2-expansion' },
        {
          id: 'nav-payment-infrastructure',
          title: 'Payment Infrastructure',
          element: 'payment-infrastructure',
        },
        {
          id: 'nav-multi-city-scaling',
          title: 'Multi-City Scaling',
          element: 'multi-city-scaling',
        },
      ];
    }

    // Vendors page sections
    if (location.pathname === '/vendors') {
      return [
        { id: 'nav-vendor-problem', title: 'The Problem', element: 'vendor-problem' },
        { id: 'nav-vendor-benefits', title: 'Vendor Benefits', element: 'vendor-benefits' },
        { id: 'nav-vendor-pricing', title: 'Pricing', element: 'vendor-pricing' },
        { id: 'nav-vendor-payment', title: 'Payment Processing', element: 'vendor-payment' },
        { id: 'nav-vendor-faq', title: 'FAQ', element: 'vendor-faq' },
        { id: 'nav-vendor-waitlist-form', title: 'Join Waitlist', element: 'vendor-waitlist-form' },
      ];
    }

    // Home page sections (default)
    return [
      { id: 'nav-hero', title: 'Welcome', element: 'hero' },
      { id: 'nav-problem', title: 'The Problem', element: 'problem' },
      { id: 'nav-features', title: 'Features', element: 'features' },
      { id: 'nav-pricing', title: 'Pricing', element: 'pricing' },

      { id: 'nav-community-treasury', title: 'Community Treasury', element: 'community-treasury' },
      { id: 'nav-governance', title: 'Governance', element: 'governance' },
      { id: 'nav-deployment', title: 'Deployment', element: 'deployment-pathway' },
      { id: 'nav-how-it-works', title: 'How It Works', element: 'how-it-works' },
      { id: 'nav-champions', title: 'Champions', element: 'champions' },
      { id: 'nav-champions-faq', title: 'Champions FAQ', element: 'champions-faq' },
      { id: 'nav-interest-form', title: 'Join Waitlist', element: 'interest-form' },
    ];
  };

  const navSections = getNavSections();
  const [currentSection, setCurrentSection] = useState<string>(navSections[0]?.element || 'hero');
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // Trigger when section is in the middle of viewport
      threshold: 0,
    };

    const observedElements = new Set<string>();

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setCurrentSection(sectionId);

          // Mark section as completed when user scrolls past it
          setCompletedSections((prev) => new Set([...prev, sectionId]));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    navSections.forEach((section) => {
      const element = document.getElementById(section.element);
      if (element) {
        observer.observe(element);
        observedElements.add(section.element);
      }
    });

    // Also check for dynamically added vendor-waitlist-form
    const checkDynamicSectionTimer = setTimeout(() => {
      const dynamicElement = document.getElementById('vendor-waitlist-form');
      if (dynamicElement && !observedElements.has('vendor-waitlist-form')) {
        observer.observe(dynamicElement);
        observedElements.add('vendor-waitlist-form');
      }
    }, 200);

    return () => {
      clearTimeout(checkDynamicSectionTimer);
      observer.disconnect();
    };
  }, [navSections]);

  const scrollToSection = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Progress Indicator - Mobile/Tablet (vertical dots on left side) */}
      <nav
        className="fixed left-1 sm:left-2 top-1/2 transform -translate-y-1/2 z-40 xl:hidden"
        aria-label="Page section navigation"
      >
        <div className="flex flex-col items-center space-y-3 bg-white/80 backdrop-blur-sm rounded-full p-1.5 sm:p-2 shadow-lg">
          <div className="flex flex-col space-y-2">
            {navSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.element)}
                className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ${
                  currentSection === section.element
                    ? 'bg-blue-600 scale-125 ring-2 ring-blue-200'
                    : completedSections.has(section.element)
                      ? 'bg-green-600'
                      : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Navigate to ${section.title} section`}
                aria-current={currentSection === section.element ? 'location' : undefined}
                title={section.title}
              />
            ))}
          </div>
        </div>
      </nav>

      {/* Quick Navigation Menu - Desktop (full menu on right side) */}
      <nav
        className="hidden xl:block fixed right-4 top-1/2 transform -translate-y-1/2 z-40"
        aria-label="Page section navigation"
      >
        <Card className="w-56 shadow-lg border-2">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 text-sm text-primary">Quick Navigation</h3>
            <div className="space-y-2" role="list">
              {navSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.element)}
                  className={`w-full text-left p-2 rounded-lg transition-all duration-200 text-sm ${
                    currentSection === section.element
                      ? 'bg-blue-100 text-blue-900 font-medium shadow-sm'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  aria-current={currentSection === section.element ? 'location' : undefined}
                  role="listitem"
                >
                  <div className="flex items-center justify-between">
                    <span>{section.title}</span>
                    {completedSections.has(section.element) && (
                      <CheckCircle
                        className="w-4 h-4 text-green-600 flex-shrink-0"
                        aria-label="Visited"
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </nav>
    </>
  );
}
